/**
 * DashboardTab — navy+gold brokerage overview.
 *
 * KOPS-465 pricing update:
 *   - Compliance is FREE for all agents — no per-seat charge
 *   - $70/mo Agent Hub is optional per agent
 *   - Monthly cost = $500 base + ($70 × agent-hub agents only)
 *   - AI Search ($135) and AI Pro ($185) are billed to the agent,
 *     not the broker — excluded from broker cost
 *
 * Sections:
 *   Row 1: 4 metric cards (Total Agents, Monthly Cost, Agent Hub Rate, Active)
 *   Row 2: Agent Plan Distribution | Monthly Broker Costs | Recent Activity
 *   Row 3: Active Transactions | Quick Actions
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faUsers,
  faDollarSign,
  faRocket,
  faChartLine,
  faArrowUp,
  faArrowDown,
  faBuilding,
  faCrown,
  faHandHoldingDollar,
  faCalendarAlt,
  faFileContract,
  faFolderOpen,
  faBolt,
  faShieldAlt,
  faSearch,
  faStar,
  faCircle,
  faUserPlus,
  faCheckCircle,
  faExchangeAlt,
} from '@fortawesome/free-solid-svg-icons';

import BrokerApiService from '../../../services/brokerApiService';
import axiosInstance from '../../../helpers/api';
import { colors, fonts, gradients } from '../shared/colors';

// KOPS-465 4-tier team plan model
const PLAN_META: Record<string, { label: string; color: string; icon: IconDefinition; price: number; paidBy: 'free' | 'broker' | 'agent' }> = {
  'team-member': { label: 'Team Member',  color: colors.muted,  icon: faBuilding,  price: 0,   paidBy: 'free' },
  'agent-hub':   { label: 'Agent Hub',    color: colors.gold,   icon: faStar,      price: 70,  paidBy: 'broker' },
  'ai-search':   { label: 'AI Search',    color: colors.blue,   icon: faSearch,    price: 135, paidBy: 'agent' },
  'ai-pro':      { label: 'AI Pro',       color: colors.green,  icon: faCrown,     price: 185, paidBy: 'agent' },
};

interface DashboardTabProps {
  brokerageId?: string;
  brokerageName?: string;
}

const DashboardTab: React.FC<DashboardTabProps> = ({ brokerageId }) => {
  const navigate = useNavigate();
  const [agents, setAgents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Active transactions
  const [transactions, setTransactions] = useState<any[]>([]);
  const [txnLoading, setTxnLoading] = useState(false);

  // Recent activity
  const [recentActivity, setRecentActivity] = useState<any[]>([]);

  const fetchDashboard = useCallback(async () => {
    if (!brokerageId) { setLoading(false); return; }
    setLoading(true);
    try {
      const agentList = await BrokerApiService.getAgents(brokerageId);
      if (Array.isArray(agentList)) setAgents(agentList);
    } catch (err: any) {
      setError(err?.message || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [brokerageId]);

  const fetchTransactions = useCallback(async () => {
    if (!brokerageId) return;
    setTxnLoading(true);
    try {
      const res = await axiosInstance.get('/transactions', {
        params: { scope: 'brokerage', brokerage_id: brokerageId, limit: 8 },
      });
      if (res.data?.success) {
        setTransactions(res.data.transactions || res.data.data || []);
      }
    } catch {
      // Silently fall back to empty — transactions may not be wired for this brokerage yet
      setTransactions([]);
    } finally {
      setTxnLoading(false);
    }
  }, [brokerageId]);

  useEffect(() => {
    fetchDashboard();
    fetchTransactions();
  }, [fetchDashboard, fetchTransactions]);

  // ---------- Derived pricing ----------
  const planCounts = useMemo(() => {
    const counts: Record<string, number> = { 'team-member': 0, 'agent-hub': 0, 'ai-search': 0, 'ai-pro': 0 };
    agents.forEach((a: any) => {
      const plan = a.team_plan || 'team-member';
      if (counts[plan] !== undefined) counts[plan]++;
      else counts['team-member']++;
    });
    return counts;
  }, [agents]);

  const activeCount = useMemo(() => agents.filter((a: any) => a.is_active || a.member_status === 'active').length, [agents]);

  // Broker only pays for agent-hub agents. AI Search / AI Pro are agent-funded.
  const agentHubCount = planCounts['agent-hub'] || 0;
  const baseFee = 500;
  const agentFees = agentHubCount * 70;
  const monthlyTotal = baseFee + agentFees;
  const agentHubRate = agents.length ? ((agentHubCount / agents.length) * 100) : 0;

  // Build recent activity from agent data
  useEffect(() => {
    const events: any[] = [];
    agents.forEach((a: any) => {
      if (a.created_at || a.joined_at) {
        const date = a.joined_at || a.created_at;
        const name = a.agent_full_name?.replace(/\s*\(Pending Invitation\)\s*$/, '') || 'Agent';
        const status = a.member_status || 'active';
        events.push({
          icon: status === 'pending' ? faUserPlus : faCheckCircle,
          color: status === 'pending' ? colors.gold : colors.green,
          text: status === 'pending' ? `${name} invited` : `${name} joined`,
          date,
        });
      }
    });
    // Sort by date descending, take top 8
    events.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    setRecentActivity(events.slice(0, 8));
  }, [agents]);

  const STAGE_COLORS: Record<string, string> = {
    pre_contract: colors.muted,
    showing: colors.blue,
    under_contract: colors.gold,
    closing: colors.green,
    completed: colors.green,
    cancelled: colors.red,
  };

  return (
    <div style={{ flex: 1, background: colors.offBg, overflowY: 'auto' }}>
      <div style={{ padding: '28px 32px', maxWidth: '1600px', margin: '0 auto' }}>

        {/* Header */}
        <div
          style={{
            background: colors.card,
            borderLeft: `4px solid ${colors.gold}`,
            borderRadius: '8px',
            padding: '20px 24px',
            marginBottom: '24px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
          }}
        >
          <h1
            style={{
              fontFamily: fonts.display,
              fontSize: '32px',
              color: colors.navy,
              margin: 0,
              letterSpacing: '0.5px',
            }}
          >
            Brokerage Overview
          </h1>
          <p style={{ color: '#555', fontSize: '14px', margin: '4px 0 0 0' }}>
            Standard Office Plan · {agents.length} agent{agents.length === 1 ? '' : 's'} ·{' '}
            <span style={{ color: colors.green, fontWeight: 600 }}>
              ${monthlyTotal.toLocaleString()}/mo
            </span>
            {' '}
            <span style={{ color: colors.muted, fontSize: '12px' }}>
              (compliance free — only Agent Hub seats billed)
            </span>
          </p>
        </div>

        {error && (
          <div
            style={{
              background: '#fdecea',
              border: `1px solid ${colors.red}`,
              borderRadius: '6px',
              padding: '12px 16px',
              color: colors.red,
              marginBottom: '16px',
              fontSize: '13px',
            }}
          >
            {error}
          </div>
        )}

        {/* Row 1 — Metric cards */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
            gap: '16px',
            marginBottom: '20px',
          }}
        >
          <MetricCard
            icon={faUsers}
            title="Total Agents"
            value={loading ? '—' : agents.length}
            subValue={`${activeCount} active · all compliance-free`}
            accent={colors.blue}
          />
          <MetricCard
            icon={faDollarSign}
            title="Monthly Broker Cost"
            value={loading ? '—' : `$${monthlyTotal.toLocaleString()}`}
            subValue={`Base $${baseFee} + ${agentHubCount} Hub seat${agentHubCount !== 1 ? 's' : ''} × $70`}
            accent={colors.green}
          />
          <MetricCard
            icon={faRocket}
            title="Agent Hub Rate"
            value={loading ? '—' : `${agentHubRate.toFixed(0)}%`}
            subValue={`${agentHubCount} of ${agents.length} on Agent Hub ($70/mo)`}
            accent={colors.gold2}
          />
          <MetricCard
            icon={faChartLine}
            title="Active Agents"
            value={loading ? '—' : activeCount}
            subValue={`${agents.length - activeCount} pending / inactive`}
            accent={colors.gold}
          />
        </div>

        {/* Row 2 — Distribution · Costs · Activity */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: '16px',
            marginBottom: '20px',
          }}
        >
          <SectionCard icon={faUsers} title="Agent Plan Distribution">
            {(['team-member', 'agent-hub', 'ai-search', 'ai-pro'] as const).map((key) => {
              const meta = PLAN_META[key];
              const count = planCounts[key] || 0;
              return (
                <div
                  key={key}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '14px',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div
                      style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        background: `${meta.color}22`,
                        border: `2px solid ${meta.color}55`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <FontAwesomeIcon icon={meta.icon} style={{ color: meta.color, fontSize: '13px' }} />
                    </div>
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: 600, color: colors.navy }}>
                        {meta.label}
                        <span style={{ fontSize: '10px', fontWeight: 500, color: colors.muted, marginLeft: '6px' }}>
                          {meta.paidBy === 'free' ? '· FREE' : meta.paidBy === 'broker' ? `· $${meta.price}/mo broker` : `· $${meta.price}/mo agent`}
                        </span>
                      </div>
                      <div style={{ fontSize: '11px', color: colors.muted }}>
                        {agents.length ? ((count / agents.length) * 100).toFixed(0) : 0}% of agents
                      </div>
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '18px', fontWeight: 700, color: meta.color }}>{count}</div>
                    <div style={{ fontSize: '10px', color: colors.muted }}>agents</div>
                  </div>
                </div>
              );
            })}
          </SectionCard>

          <SectionCard icon={faHandHoldingDollar} title="Monthly Broker Costs">
            <div style={{ marginBottom: '18px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '4px' }}>
                <span style={{ color: colors.muted, fontSize: '12px' }}>Total Monthly</span>
                <span style={{ color: colors.green, fontSize: '24px', fontWeight: 700 }}>
                  ${monthlyTotal.toLocaleString()}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <span style={{ color: colors.muted, fontSize: '12px' }}>Annual Projection</span>
                <span style={{ color: colors.green, fontSize: '16px', fontWeight: 600 }}>
                  ${(monthlyTotal * 12).toLocaleString()}
                </span>
              </div>
            </div>

            <CostBar label="Platform Access (Base)" amount={baseFee} total={monthlyTotal} color={colors.gold2} />
            <CostBar
              label={`Agent Hub Seats (${agentHubCount} × $70)`}
              amount={agentFees}
              total={monthlyTotal}
              color={colors.blue}
            />

            <div
              style={{
                marginTop: '14px',
                padding: '10px 12px',
                background: `${colors.green}0a`,
                border: `1px solid ${colors.green}33`,
                borderRadius: '6px',
                fontSize: '11px',
                color: colors.green,
                lineHeight: 1.5,
              }}
            >
              <strong>Compliance is free</strong> for all agents. Agent Hub ($70/mo) is
              optional — covers CRM + MLS search. AI Search / AI Pro are billed
              to the agent directly.
            </div>
          </SectionCard>

          <SectionCard icon={faCalendarAlt} title="Recent Activity">
            {recentActivity.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '24px 0', color: colors.muted }}>
                <FontAwesomeIcon icon={faCalendarAlt} style={{ fontSize: '24px', marginBottom: '8px' }} />
                <div style={{ fontSize: '13px' }}>No recent activity to display</div>
              </div>
            ) : (
              <div>
                {recentActivity.map((ev, i) => (
                  <div
                    key={i}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '8px 0',
                      borderBottom: i < recentActivity.length - 1 ? `1px solid ${colors.border}` : 'none',
                    }}
                  >
                    <FontAwesomeIcon icon={ev.icon} style={{ color: ev.color, fontSize: '12px', width: '16px' }} />
                    <div style={{ flex: 1, fontSize: '12px', color: colors.navy }}>{ev.text}</div>
                    <div style={{ fontSize: '10px', color: colors.muted, whiteSpace: 'nowrap' }}>
                      {new Date(ev.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </SectionCard>
        </div>

        {/* Row 3 — Active Transactions + Quick Actions */}
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '16px' }}>
          <SectionCard icon={faFileContract} title={`Active Transactions (${transactions.length})`}>
            {txnLoading ? (
              <div style={{ textAlign: 'center', padding: '24px 0', color: colors.muted, fontSize: '12px' }}>
                Loading transactions…
              </div>
            ) : transactions.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '32px 0', color: colors.muted }}>
                <FontAwesomeIcon icon={faFileContract} style={{ fontSize: '28px', marginBottom: '8px', color: colors.gold3 }} />
                <div style={{ fontSize: '13px' }}>No active transactions right now</div>
                <div style={{ fontSize: '11px', marginTop: '4px' }}>
                  Click the Transactions tab for the full pipeline
                </div>
              </div>
            ) : (
              <div style={{ display: 'grid', gap: '6px' }}>
                {transactions.slice(0, 8).map((t: any) => {
                  const stage = t.stage || 'pre_contract';
                  const stageColor = STAGE_COLORS[stage] || colors.muted;
                  const pd = typeof t.property_data === 'string' ? (() => { try { return JSON.parse(t.property_data); } catch { return null; } })() : t.property_data;
                  const address = pd?.address || pd?.street_address || t.client_name || t.file_number || 'Untitled';
                  return (
                    <div
                      key={t.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '8px 12px',
                        background: colors.offBg,
                        borderRadius: '6px',
                        borderLeft: `3px solid ${stageColor}`,
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '12px', fontWeight: 600, color: colors.navy, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {address}
                        </div>
                        <div style={{ fontSize: '10px', color: colors.muted }}>
                          {t.file_number ? `#${t.file_number} · ` : ''}{t.client_name || '—'}
                        </div>
                      </div>
                      <span
                        style={{
                          fontSize: '9px',
                          fontWeight: 700,
                          padding: '3px 8px',
                          borderRadius: '4px',
                          textTransform: 'uppercase',
                          background: `${stageColor}22`,
                          color: stageColor,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {stage.replace(/_/g, ' ')}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </SectionCard>

          <SectionCard icon={faBolt} title="Quick Actions">
            <QuickAction icon={faFileContract} label="View Transactions" onClick={() => navigate('/broker-intelligence/transactions')} />
            <QuickAction icon={faShieldAlt} label="Compliance Center" onClick={() => navigate('/broker-intelligence/compliance')} />
            <QuickAction icon={faFolderOpen} label="Document Templates" onClick={() => navigate('/broker-intelligence/documents')} />
            <QuickAction icon={faBolt} label="AI Review Queue" onClick={() => navigate('/broker-intelligence/ai-review')} />
          </SectionCard>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Sub-components
// ============================================================================

const MetricCard: React.FC<{
  icon: IconDefinition;
  title: string;
  value: string | number;
  subValue?: string;
  change?: number;
  accent: string;
}> = ({ icon, title, value, subValue, change, accent }) => (
  <div
    style={{
      background: colors.white,
      border: `1px solid ${colors.border}`,
      borderRadius: '10px',
      padding: '20px',
      borderLeft: `4px solid ${accent}`,
      boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
    }}
  >
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '14px' }}>
      <div
        style={{
          width: '44px',
          height: '44px',
          borderRadius: '10px',
          background: `linear-gradient(135deg, ${accent} 0%, ${accent}cc 100%)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: `0 4px 10px ${accent}33`,
        }}
      >
        <FontAwesomeIcon icon={icon} style={{ color: colors.white, fontSize: '18px' }} />
      </div>
      {change !== undefined && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            background: change >= 0 ? `${colors.green}15` : `${colors.red}15`,
            color: change >= 0 ? colors.green : colors.red,
            padding: '3px 8px',
            borderRadius: '10px',
            fontSize: '11px',
            fontWeight: 700,
          }}
        >
          <FontAwesomeIcon icon={change >= 0 ? faArrowUp : faArrowDown} />
          {Math.abs(change)}%
        </div>
      )}
    </div>
    <div style={{ fontFamily: fonts.display, fontSize: '32px', color: colors.navy, marginBottom: '4px', letterSpacing: '0.5px', lineHeight: 1 }}>
      {value}
    </div>
    <div style={{ fontSize: '12px', fontWeight: 600, color: colors.navy, marginBottom: '2px' }}>{title}</div>
    {subValue && <div style={{ fontSize: '11px', color: colors.muted }}>{subValue}</div>}
  </div>
);

const SectionCard: React.FC<{ icon: IconDefinition; title: string; children: React.ReactNode }> = ({ icon, title, children }) => (
  <div
    style={{
      background: colors.white,
      border: `1px solid ${colors.border}`,
      borderLeft: `4px solid ${colors.gold}`,
      borderRadius: '10px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
    }}
  >
    <div style={{ padding: '14px 20px', borderBottom: `1px solid ${colors.border}`, display: 'flex', alignItems: 'center', gap: '10px' }}>
      <FontAwesomeIcon icon={icon} style={{ color: colors.gold, fontSize: '14px' }} />
      <h5 style={{ margin: 0, fontSize: '11px', fontWeight: 700, color: colors.navy, textTransform: 'uppercase', letterSpacing: '1px' }}>
        {title}
      </h5>
    </div>
    <div style={{ padding: '18px 20px' }}>{children}</div>
  </div>
);

const CostBar: React.FC<{ label: string; amount: number; total: number; color: string }> = ({ label, amount, total, color }) => {
  const pct = total > 0 ? (amount / total) * 100 : 0;
  return (
    <div style={{ marginBottom: '10px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', fontSize: '11px' }}>
        <span style={{ color: colors.muted }}>{label}</span>
        <span style={{ fontWeight: 600, color: colors.navy }}>${amount.toLocaleString()}</span>
      </div>
      <div style={{ height: '6px', background: colors.border, borderRadius: '3px', overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, transition: 'width 0.3s' }} />
      </div>
    </div>
  );
};

const QuickAction: React.FC<{ icon: IconDefinition; label: string; onClick: () => void }> = ({ icon, label, onClick }) => (
  <button
    onClick={onClick}
    style={{
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      padding: '12px 14px',
      marginBottom: '8px',
      background: gradients.goldButton,
      border: 'none',
      borderRadius: '8px',
      color: colors.navy,
      fontSize: '13px',
      fontWeight: 600,
      cursor: 'pointer',
      fontFamily: fonts.body,
      textAlign: 'left',
    }}
  >
    <FontAwesomeIcon icon={icon} />
    <span>{label}</span>
  </button>
);

export default DashboardTab;
